import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Package,
  AlertTriangle,
  CheckCircle2,
  X,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { usePlatformGearModels } from '@/hooks/use-platform';
import type { PlatformGearModelListItem } from '@/lib/types';

const ASSET_CATEGORIES = [
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'harness', label: 'Harness' },
  { value: 'rope', label: 'Rope' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'anchor_strap', label: 'Anchor Strap' },
  { value: 'connector', label: 'Connector' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'fall_arrest_accessory', label: 'Fall Arrest Accessory' },
  { value: 'mobile_fall_arrester', label: 'Mobile Fall Arrester' },
  { value: 'rigging_plate', label: 'Rigging Plate' },
  { value: 'rope_clamp', label: 'Rope Clamp' },
  { value: 'swivel', label: 'Swivel' },
  { value: 'other', label: 'Other' },
];

const PAGE_SIZE = 20;

export function GearModelsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || '';
  const assetCategory = searchParams.get('asset_category') || '';

  const { data, isLoading, isError } = usePlatformGearModels({
    search: search || undefined,
    asset_category: assetCategory || undefined,
    page,
  });

  const gearModels = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ search: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
    setSearchInput('');
  }, [setSearchParams]);

  const hasActiveFilters = search || assetCategory;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gear Models</h1>
          <p className="text-gray-600 mt-1">
            Manage global gear model catalog and inspection templates
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <Button asChild>
          <Link to="/platform/gear-models/new">
            <Plus className="h-4 w-4 mr-2" />
            Add Gear Model
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by name, manufacturer, or model number..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10"
              />
            </div>
            <Select
              value={assetCategory || 'all'}
              onValueChange={(value) =>
                updateSearchParams({ asset_category: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {ASSET_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSearch}>Search</Button>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="text-sm">
              <X className="h-4 w-4 mr-2" />
              Clear all filters
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              Failed to load gear models. Please try again.
            </div>
          ) : gearModels.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">
                {hasActiveFilters ? 'No gear models match your search' : 'No gear models yet'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Add your first gear model to start building the catalog'}
              </p>
              <div className="flex items-center justify-center gap-3 mt-4">
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-2" />
                    Clear filters
                  </Button>
                )}
                <Button asChild>
                  <Link to="/platform/gear-models/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Gear Model
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Model #</TableHead>
                  <TableHead className="text-center">Steps</TableHead>
                  <TableHead>Service Life</TableHead>
                  <TableHead>Inspection Interval</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gearModels.map((model) => (
                  <GearModelRow key={model.id} model={model} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() => updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

function GearModelRow({ model }: { model: PlatformGearModelListItem }) {
  return (
    <TableRow className="hover:bg-gray-50">
      <TableCell>
        <Link
          to={`/platform/gear-models/${model.id}`}
          className="font-medium text-blue-600 hover:underline"
        >
          {model.name}
        </Link>
      </TableCell>
      <TableCell className="text-gray-600">{model.manufacturer_name}</TableCell>
      <TableCell>
        <Badge variant="outline" className="capitalize">
          {model.asset_category_display}
        </Badge>
      </TableCell>
      <TableCell className="text-gray-600">{model.model_number || '—'}</TableCell>
      <TableCell className="text-center">
        <Badge variant={model.step_count > 0 ? 'default' : 'secondary'}>
          {model.step_count}
        </Badge>
      </TableCell>
      <TableCell className="text-gray-600">
        {model.service_life_years ? `${model.service_life_years} yrs` : '—'}
      </TableCell>
      <TableCell className="text-gray-600">
        {model.default_inspection_interval_days
          ? `${model.default_inspection_interval_days} days`
          : '—'}
      </TableCell>
      <TableCell className="text-center">
        {model.is_verified ? (
          <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto" />
        ) : (
          <span className="text-xs text-gray-400">Unverified</span>
        )}
      </TableCell>
    </TableRow>
  );
}
